import { useMemo } from 'react'
import { createTheme, ThemeOptions } from '@mui/material/styles'
import { useThemeMode } from '../contexts/ThemeContext'
import designSystem from '../theme/designSystem'

// Extinde interfața Theme pentru proprietăți custom
declare module '@mui/material/styles' {
  interface Theme {
    designSystem: typeof designSystem
  }
  interface ThemeOptions {
    designSystem?: typeof designSystem
  }
}

export const useAppTheme = () => {
  const { isDarkMode } = useThemeMode()

  const theme = useMemo(() => {
    const colors = isDarkMode ? designSystem.colors.dark : designSystem.colors.light
    
    const baseTheme: ThemeOptions = {
      // Incorporăm design system-ul în theme
      designSystem,
      
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        
        // Primary folosește accent-ul nostru
        primary: {
          main: colors.accent.primary,
          light: colors.accent.primary,
          dark: colors.accent.hover,
          contrastText: '#ffffff',
        },
        
        // Secondary pentru text secundar
        secondary: {
          main: colors.text.secondary,
          light: colors.text.secondary,
          dark: colors.text.primary,
          contrastText: '#ffffff', // Contrast mai bun pentru secondary buttons
        },
        
        // Status colors folosind paleta noastră
        error: {
          main: designSystem.colors.status.error,
          light: designSystem.colors.status.error + '20', // 20% opacity
          dark: designSystem.colors.status.error,
          contrastText: '#ffffff',
        },
        warning: {
          main: designSystem.colors.status.warning,
          light: designSystem.colors.status.warning + '20',
          dark: designSystem.colors.status.warning,
          contrastText: '#ffffff',
        },
        info: {
          main: designSystem.colors.status.info,
          light: designSystem.colors.status.info + '20',
          dark: designSystem.colors.status.info,
          contrastText: '#ffffff',
        },
        success: {
          main: designSystem.colors.status.success,
          light: designSystem.colors.status.success + '20',
          dark: designSystem.colors.status.success,
          contrastText: '#ffffff',
        },
        
        // Background folosind paleta noastră
        background: {
          default: colors.background.default,
          paper: colors.background.paper,
        },
        
        // Text folosind paleta noastră
        text: {
          primary: colors.text.primary,
          secondary: colors.text.secondary,
          disabled: colors.text.disabled,
        },
        
        // Borders
        divider: colors.border.default,
      },
      
      // Typography bazată pe design system
      typography: {
        fontFamily: designSystem.typography.fontFamily,
        h1: designSystem.typography.variants.h1,
        h2: designSystem.typography.variants.h2,
        h3: designSystem.typography.variants.h3,
        h4: designSystem.typography.variants.h4,
        h5: designSystem.typography.variants.h5,
        h6: designSystem.typography.variants.h6,
        body1: designSystem.typography.variants.body1,
        body2: designSystem.typography.variants.body2,
        caption: designSystem.typography.variants.caption,
        button: designSystem.typography.variants.button,
      },
      
      // Shape bazat pe design system
      shape: {
        borderRadius: designSystem.components.card.borderRadius,
      },
      
      // Spacing bazat pe design system  
      spacing: designSystem.spacing.unit,
      
      // Component overrides pentru standardizare completă
      components: {
        // Card styling
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: designSystem.components.card.borderRadius,
              padding: designSystem.components.card.padding,
              boxShadow: designSystem.shadows.sm,
              border: `1px solid ${colors.border.default}`,
              '&:hover': {
                boxShadow: designSystem.shadows.md,
              },
            },
          },
        },
        
        // Button styling standardizat
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: designSystem.components.button.borderRadius,
              height: designSystem.components.button.height.medium,
              padding: `${designSystem.components.button.padding.vertical}px ${designSystem.components.button.padding.horizontal}px`,
              textTransform: designSystem.components.button.textTransform,
              fontWeight: designSystem.components.button.fontWeight,
              fontSize: designSystem.typography.variants.button.fontSize,
              letterSpacing: designSystem.typography.variants.button.letterSpacing,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: designSystem.shadows.sm,
              },
            },
            containedPrimary: {
              backgroundColor: colors.accent.primary,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: colors.accent.hover,
              },
            },
            containedSecondary: {
              backgroundColor: 'transparent',
              color: colors.text.primary,
              border: `1px solid ${colors.border.light}`,
              '&:hover': {
                backgroundColor: colors.background.elevated,
                borderColor: colors.accent.primary,
              },
            },
          },
        },
        
        // Input styling standardizat
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: designSystem.components.input.borderRadius,
                fontSize: designSystem.typography.variants.body1.fontSize,
                '& fieldset': {
                  borderColor: colors.border.default,
                },
                '&:hover fieldset': {
                  borderColor: colors.border.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.border.focus,
                  borderWidth: '2px',
                },
              },
              // IMPORTANT: multiline inputs must NOT have fixed height
              // (otherwise textarea collapses and looks broken)
              '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': {
                height: designSystem.components.input.height,
                // ✅ Allow compact inputs (filters/searchbars) via size="small"
                '&.MuiInputBase-sizeSmall': {
                  height: 32,
                  minHeight: 32,
                },
              },
              '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
                height: 'auto',
              },
              '& .MuiInputLabel-root': {
                fontSize: designSystem.typography.variants.body2.fontSize,
                fontWeight: 500,
                color: colors.text.secondary,
              },
            },
          },
        },
        
        // AppBar styling
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: colors.background.paper,
              color: colors.text.primary,
              boxShadow: designSystem.shadows.sm,
              borderBottom: `1px solid ${colors.border.default}`,
            },
          },
        },
        
        // Drawer styling
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: colors.background.paper,
              borderRight: `1px solid ${colors.border.default}`,
              boxShadow: designSystem.shadows.sm,
            },
          },
        },
        
        // Chip styling
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: designSystem.components.button.borderRadius,
              fontWeight: 500,
              fontSize: designSystem.typography.variants.caption.fontSize,
            },
            colorSuccess: {
              backgroundColor: designSystem.colors.status.success + '20',
              color: designSystem.colors.status.success,
              border: `1px solid ${designSystem.colors.status.success}40`,
            },
            colorError: {
              backgroundColor: designSystem.colors.status.error + '20',
              color: designSystem.colors.status.error,
              border: `1px solid ${designSystem.colors.status.error}40`,
            },
            colorWarning: {
              backgroundColor: designSystem.colors.status.warning + '20',
              color: designSystem.colors.status.warning,
              border: `1px solid ${designSystem.colors.status.warning}40`,
            },
            colorInfo: {
              backgroundColor: designSystem.colors.status.info + '20',
              color: designSystem.colors.status.info,
              border: `1px solid ${designSystem.colors.status.info}40`,
            },
          },
        },
        
        // Paper styling pentru consistență
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: colors.background.paper,
              border: `1px solid ${colors.border.default}`,
              // Contrast mai bun pentru light theme
              ...(!isDarkMode && {
                boxShadow: designSystem.shadows.sm,
              }),
            },
          },
        },
        
        // Table styling
        MuiTableContainer: {
          styleOverrides: {
            root: {
              borderRadius: designSystem.components.card.borderRadius,
              border: `1px solid ${colors.border.default}`,
            },
          },
        },
        
        MuiTableHead: {
          styleOverrides: {
            root: {
              backgroundColor: colors.background.elevated,
              '& .MuiTableCell-head': {
                fontWeight: 600,
                fontSize: designSystem.typography.variants.body2.fontSize,
                color: colors.text.primary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              },
            },
          },
        },
        
        // Tab styling pentru pages cu tab-uri
        MuiTabs: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${colors.border.default}`,
              minHeight: 48,
            },
            indicator: {
              backgroundColor: colors.accent.primary,
              height: 3,
            },
          },
        },
        
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontSize: designSystem.typography.variants.body1.fontSize,
              fontWeight: 500,
              color: colors.text.secondary,
              minHeight: 48,
              '&.Mui-selected': {
                color: colors.text.primary,
                fontWeight: 600,
              },
            },
          },
        },
      },
    }

    return createTheme(baseTheme)
  }, [isDarkMode])

  return theme
}
